import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import Grow from '../../../../components/grow';
import useOnClickOutside from '../../../../hooks/useOnClickOutside';
import { TechnicianIcon } from '../../../../assets/icons/TechnicianIcon';
import { DialogBody } from '../../../../stateManagement/reducers/confirmDialogReducer';
import { useConfirmationDialog } from '../../../../hooks/useConfirmationDialog';
import InternalTaskService from '../../../../services/InternalTaskService';
import NotificationService from '../../../../services/NotificationService';
import { Container, ExpandedToolMenu, MenuTrigger } from '../../../../styling/ListToolsStyling';
import { setShouldUpdateNumberOfInternalTasksCreated } from '../../../../stateManagement/reducers/taskListReducer';

interface Props {
  selectedTaskIds: number[];
  fetchTasksCallback: () => void;
  setIsLoading: (isLoading: boolean) => void;
}

const InternalTaskPlanningSystemMenu = (props: Props) => {
  const { selectedTaskIds, fetchTasksCallback, setIsLoading } = props;

  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  const { getConfirmation } = useConfirmationDialog();
  const rootRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(rootRef, () => setOpen(false));

  const handlePlanWithSystem = useCallback(
    async (systemName: 'FLS' | 'D365') => {
      const dialogBody: DialogBody = {
        headerText:
          selectedTaskIds.length > 1
            ? `Vil du sende ${selectedTaskIds.length} opgaver til ${systemName}?`
            : `Vil du sende opgaven til ${systemName}?`,
        bodyText: '',
        declineButtonText: 'Fortryd',
        confirmButtonText: 'Bekræft'
      };

      const confirmation = await getConfirmation(dialogBody);

      if (confirmation === 'confirm') {
        setIsLoading(true);
        setOpen(false);

        const serviceCall = systemName === 'FLS' 
          ? InternalTaskService.sendInternalTasksToPlanning(selectedTaskIds)
          : InternalTaskService.planInternalTasksWithD365(selectedTaskIds);

        serviceCall
          .then(() => {
            fetchTasksCallback();
            dispatch(setShouldUpdateNumberOfInternalTasksCreated(true));
            NotificationService.success(
              selectedTaskIds.length > 1 
                ? `Sendte ${selectedTaskIds.length} opgaver til ${systemName}.` 
                : `Opgaven er sendt til ${systemName}`
            );
          })
          .catch((error) => {
            let errorMessage;
            if (error.status === 400) {
              errorMessage = 'Ugyldig status på en eller flere opgaver. Opdater din driftsliste.';
            } else if (error.status === 404) {
              errorMessage = `Opgavetypen findes ikke i ${systemName}. Kontakt administrator hvis dette ikke er korrekt.`;
            } else {
              errorMessage = `Der opstod en fejl i afsendelsen af opgaver: ${error}`;
            }
            NotificationService.error(errorMessage);
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        setOpen(false);
      }
    },
    [selectedTaskIds, fetchTasksCallback, setIsLoading, getConfirmation, dispatch]
  );

  return (
    <Container ref={rootRef}>
      <MenuTrigger onClick={() => setOpen(!open)} variant="primary" square data-testid="planning-system-button">
        <TechnicianIcon size="24px" />
        Planlæg med...
      </MenuTrigger>

      <Grow in={open}>
        <ExpandedToolMenu>
          <MenuItem onClick={() => handlePlanWithSystem('FLS')} data-testid="plan-with-fls">
            <strong>FLS</strong>
          </MenuItem>
          <MenuItem onClick={() => handlePlanWithSystem('D365')} data-testid="plan-with-d365">
            <strong>D365</strong>
          </MenuItem>
        </ExpandedToolMenu>
      </Grow>
    </Container>
  );
};

const MenuItem = styled.span`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
  background-color: ${(props) => props.theme.palette.main.yellow.primary};
  color: ${(props) => props.theme.palette.main.black.primary};
  border-radius: 8px;
  margin: 4px 0;

  &:hover {
    background-color: ${(props) => props.theme.palette.main.yellow.secondary};
  }
`;

export default InternalTaskPlanningSystemMenu;
